import React, { useEffect, useState } from 'react';
import { MessageCard } from './MessageCard';
import { peopleProfiles, aiMessageTemplates } from '../utils/mockData';
import { MessageSquareTextIcon, RefreshCwIcon, PlusIcon, SaveIcon, SendIcon, ListIcon, CheckIcon, ArrowLeftIcon, ChevronDownIcon } from 'lucide-react';
import { MessageTemplateSelector } from './MessageTemplateSelector';
import { EngagementContext } from './EngagementContext';
import { OutreachSequence } from './OutreachSequence';
interface MessagesProps {
  selectedPersonId: number | null;
  interestingPeople: number[];
}
export const Messages: React.FC<MessagesProps> = ({
  selectedPersonId,
  interestingPeople
}) => {
  const [selectedPerson, setSelectedPerson] = useState<number | null>(selectedPersonId);
  const [generating, setGenerating] = useState(false);
  const [contextInput, setContextInput] = useState('');
  const [tone, setTone] = useState('professional');
  const [messageLength, setMessageLength] = useState('medium');
  const [callToAction, setCallToAction] = useState('meeting');
  const [savedMessages, setSavedMessages] = useState<any[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSequence, setShowSequence] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<number | null>(null);
  const [showMobileOptions, setShowMobileOptions] = useState(false);
  const [showMobileSettings, setShowMobileSettings] = useState(false);
  useEffect(() => {
    if (selectedPersonId) {
      setSelectedPerson(selectedPersonId);
    }
  }, [selectedPersonId]);
  const relevantPeople = peopleProfiles.filter(person => interestingPeople.includes(person.id));
  const getMessagesForPerson = (personId: number) => {
    return [...aiMessageTemplates.filter(message => message.personId === personId), ...savedMessages.filter(message => message.personId === personId)];
  };
  const handleGenerateMessage = (additionalContext?: string) => {
    setGenerating(true);
    // Simulate message generation delay
    setTimeout(() => {
      // In a real app, this would call an API to generate a message based on contextInput and settings
      const newMessage = {
        id: aiMessageTemplates.length + savedMessages.length + 1,
        personId: selectedPerson || 0,
        postId: 0,
        subject: `New AI-generated message for ${relevantPeople.find(p => p.id === selectedPerson)?.name}`,
        message: `Hi ${relevantPeople.find(p => p.id === selectedPerson)?.name.split(' ')[0]},\n\nThis is a custom generated message based on your settings. It's using a ${tone} tone, ${messageLength} length, and includes a ${callToAction} call to action.\n\n${additionalContext ? `Based on context: ${additionalContext}\n\n` : ''}${contextInput ? `I've included your specific context: ${contextInput}\n\n` : ''}Would you be interested in discussing how our solution could help your team at ${relevantPeople.find(p => p.id === selectedPerson)?.company}?\n\nBest regards,\n[Your Name]`
      };
      setSavedMessages(prev => [...prev, newMessage]);
      setGenerating(false);
      setContextInput('');
      setSelectedMessage(newMessage.id);
    }, 1500);
  };
  const handleSelectTemplate = (templateText: string) => {
    if (!selectedPerson) return;
    // Replace placeholders with actual data
    const person = relevantPeople.find(p => p.id === selectedPerson);
    if (!person) return;
    let personalizedTemplate = templateText.replace(/\[Name\]/g, person.name.split(' ')[0]).replace(/\[Their Company\]/g, person.company);
    // Create a new message from the template
    const newMessage = {
      id: aiMessageTemplates.length + savedMessages.length + 1,
      personId: selectedPerson,
      postId: 0,
      subject: `Template message for ${person.name}`,
      message: personalizedTemplate
    };
    setSavedMessages(prev => [...prev, newMessage]);
    setShowTemplates(false);
    setSelectedMessage(newMessage.id);
  };
  const handleSaveMessage = (messageId: number, updatedText: string) => {
    setSavedMessages(prev => prev.map(msg => msg.id === messageId ? {
      ...msg,
      message: updatedText
    } : msg));
  };
  const handleSendMessage = () => {
    alert('Message sent successfully! (This is a simulation)');
  };
  const handleCreateSequence = () => {
    if (!selectedMessage) {
      alert('Please select a message to use as the first step in your sequence');
      return;
    }
    setShowSequence(true);
  };
  const selectedMessageText = selectedMessage ? [...aiMessageTemplates, ...savedMessages].find(m => m.id === selectedMessage)?.message || '' : '';
  const selectedPersonName = relevantPeople.find(p => p.id === selectedPerson)?.name || '';
  const toggleMobileOptions = () => {
    setShowMobileOptions(!showMobileOptions);
    if (showMobileSettings) setShowMobileSettings(false);
  };
  const toggleMobileSettings = () => {
    setShowMobileSettings(!showMobileSettings);
    if (showMobileOptions) setShowMobileOptions(false);
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-428">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 sm:gap-0" data-id="element-429">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-430">
          AI Outreach Messages
        </h1>
        {/* Desktop actions */}
        <div className="hidden sm:flex space-x-2" data-id="element-431">
          <button onClick={() => setShowTemplates(!showTemplates)} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-432">
            <PlusIcon size={16} className="mr-2" data-id="element-433" />
            Templates
          </button>
          <button onClick={handleCreateSequence} disabled={!selectedMessage} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50" data-id="element-434">
            <ListIcon size={16} className="mr-2" data-id="element-435" />
            Create Sequence
          </button>
          <button onClick={handleSendMessage} disabled={!selectedPerson || getMessagesForPerson(selectedPerson).length === 0 || !selectedMessage} className="inline-flex items-center px-4 py-2 bg-[#f9b30f] hover:bg-[#e0a40e] text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-436">
            <SendIcon size={16} className="mr-2" data-id="element-437" />
            Send Message
          </button>
        </div>
        {/* Mobile actions */}
        <div className="flex sm:hidden space-x-2" data-id="element-438">
          <button onClick={toggleMobileOptions} className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-439">
            Options
            <ChevronDownIcon size={16} className="ml-1" data-id="element-440" />
          </button>
          <button onClick={handleSendMessage} disabled={!selectedPerson || getMessagesForPerson(selectedPerson).length === 0 || !selectedMessage} className="inline-flex items-center px-3 py-1.5 bg-[#f9b30f] hover:bg-[#e0a40e] text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-441">
            <SendIcon size={16} className="mr-1" data-id="element-442" />
            Send
          </button>
        </div>
      </div>
      {/* Mobile options dropdown */}
      {showMobileOptions && <div className="sm:hidden mb-4 p-3 bg-white rounded-lg shadow-sm border border-gray-200" data-id="element-443">
          <div className="space-y-2" data-id="element-444">
            <button onClick={() => {
          setShowTemplates(!showTemplates);
          setShowMobileOptions(false);
        }} className="w-full text-left px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-445">
              <PlusIcon size={16} className="inline mr-2" data-id="element-446" />
              Message Templates
            </button>
            <button onClick={() => {
          if (!selectedMessage) {
            alert('Please select a message first');
            return;
          }
          handleCreateSequence();
          setShowMobileOptions(false);
        }} className="w-full text-left px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-447">
              <ListIcon size={16} className="inline mr-2" data-id="element-448" />
              Create Sequence
            </button>
            <button onClick={toggleMobileSettings} className="w-full text-left px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-449">
              <CheckIcon size={16} className="inline mr-2" data-id="element-450" />
              Message Settings
            </button>
          </div>
        </div>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-451">
        <div className="lg:col-span-1" data-id="element-452">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-453">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-454">
              Select Person
            </h2>
            <div className="space-y-2" data-id="element-455">
              <select value={selectedPerson || ''} onChange={e => {
              setSelectedPerson(Number(e.target.value) || null);
              setSelectedMessage(null);
            }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" data-id="element-456">
                <option value="" data-id="element-457">Select a person</option>
                {relevantPeople.map(person => <option key={person.id} value={person.id} data-id="element-458">
                    {person.name} - {person.title}
                  </option>)}
              </select>
            </div>
            {selectedPerson && <div className="mt-4" data-id="element-459">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-460">
                  Additional Context
                </h3>
                <textarea value={contextInput} onChange={e => setContextInput(e.target.value)} placeholder="Add any specific information you'd like to include in the outreach message..." className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm" rows={4} data-id="element-461" />
                <button onClick={() => handleGenerateMessage()} disabled={generating} className="mt-3 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#f9b30f] hover:bg-[#e0a40e] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f9b30f] disabled:opacity-50" data-id="element-462">
                  {generating ? <>
                      <RefreshCwIcon size={16} className="mr-2 animate-spin" data-id="element-463" />
                      Generating...
                    </> : 'Generate New Message'}
                </button>
              </div>}
          </div>
          {selectedPerson && <div className="hidden lg:block" data-id="element-464">
              <EngagementContext personId={selectedPerson} onGenerateMessage={handleGenerateMessage} data-id="element-465" />
            </div>}
          {/* Desktop message settings */}
          <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-466">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-467">
              Message Settings
            </h2>
            <div className="space-y-4" data-id="element-468">
              <div data-id="element-469">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-470">
                  Tone
                </label>
                <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" value={tone} onChange={e => setTone(e.target.value)} data-id="element-471">
                  <option value="professional" data-id="element-472">Professional</option>
                  <option value="casual" data-id="element-473">Casual</option>
                  <option value="formal" data-id="element-474">Formal</option>
                  <option value="friendly" data-id="element-475">Friendly</option>
                </select>
              </div>
              <div data-id="element-476">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-477">
                  Message Length
                </label>
                <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" value={messageLength} onChange={e => setMessageLength(e.target.value)} data-id="element-478">
                  <option value="short" data-id="element-479">Short (50-100 words)</option>
                  <option value="medium" data-id="element-480">Medium (100-150 words)</option>
                  <option value="long" data-id="element-481">Long (150-200 words)</option>
                </select>
              </div>
              <div data-id="element-482">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-483">
                  Call to Action
                </label>
                <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" value={callToAction} onChange={e => setCallToAction(e.target.value)} data-id="element-484">
                  <option value="meeting" data-id="element-485">Request Meeting</option>
                  <option value="feedback" data-id="element-486">Ask for Feedback</option>
                  <option value="resource" data-id="element-487">Share Resource</option>
                  <option value="connect" data-id="element-488">Connect Only</option>
                </select>
              </div>
            </div>
          </div>
          {/* Mobile message settings */}
          {showMobileSettings && <div className="lg:hidden bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-489">
              <div className="flex justify-between items-center mb-4" data-id="element-490">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-491">
                  Message Settings
                </h2>
                <button onClick={toggleMobileSettings} className="text-gray-400 hover:text-gray-600" data-id="element-492">
                  <ArrowLeftIcon size={16} data-id="element-493" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-494">
                <div data-id="element-495">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-496">
                    Tone
                  </label>
                  <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" value={tone} onChange={e => setTone(e.target.value)} data-id="element-497">
                    <option value="professional" data-id="element-498">Professional</option>
                    <option value="casual" data-id="element-499">Casual</option>
                    <option value="formal" data-id="element-500">Formal</option>
                    <option value="friendly" data-id="element-501">Friendly</option>
                  </select>
                </div>
                <div data-id="element-502">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-503">
                    Message Length
                  </label>
                  <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" value={messageLength} onChange={e => setMessageLength(e.target.value)} data-id="element-504">
                    <option value="short" data-id="element-505">Short (50-100 words)</option>
                    <option value="medium" data-id="element-506">Medium (100-150 words)</option>
                    <option value="long" data-id="element-507">Long (150-200 words)</option>
                  </select>
                </div>
                <div data-id="element-508">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-509">
                    Call to Action
                  </label>
                  <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" value={callToAction} onChange={e => setCallToAction(e.target.value)} data-id="element-510">
                    <option value="meeting" data-id="element-511">Request Meeting</option>
                    <option value="feedback" data-id="element-512">Ask for Feedback</option>
                    <option value="resource" data-id="element-513">Share Resource</option>
                    <option value="connect" data-id="element-514">Connect Only</option>
                  </select>
                </div>
              </div>
            </div>}
          {showTemplates && <div className="mt-6" data-id="element-515">
              <MessageTemplateSelector onSelectTemplate={handleSelectTemplate} data-id="element-516" />
            </div>}
        </div>
        <div className="lg:col-span-2" data-id="element-517">
          {/* Mobile engagement context */}
          {selectedPerson && <div className="lg:hidden mb-6" data-id="element-518">
              <EngagementContext personId={selectedPerson} onGenerateMessage={handleGenerateMessage} data-id="element-519" />
            </div>}
          {selectedPerson ? showSequence ? <OutreachSequence personId={selectedPerson} personName={selectedPersonName} initialMessage={selectedMessageText} data-id="element-520" /> : <>
                <div className="mb-4" data-id="element-521">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-522">
                    Message Templates for{' '}
                    {relevantPeople.find(p => p.id === selectedPerson)?.name}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-523">
                    Select a message to use it for outreach or create a sequence
                  </p>
                </div>
                <div className="space-y-4" data-id="element-524">
                  {getMessagesForPerson(selectedPerson).map(message => <MessageCard key={message.id} message={message} onSave={handleSaveMessage} isSelected={selectedMessage === message.id} onSelect={() => setSelectedMessage(message.id)} data-id="element-525" />)}
                  {getMessagesForPerson(selectedPerson).length === 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-526">
                      <MessageSquareTextIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-527" />
                      <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-528">
                        No messages generated yet
                      </h3>
                      <p className="text-gray-500" data-id="element-529">
                        Add context and click "Generate New Message" to create
                        outreach templates.
                      </p>
                    </div>}
                </div>
              </> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center h-64 flex flex-col items-center justify-center" data-id="element-530">
              <MessageSquareTextIcon size={48} className="text-gray-400 mb-4" data-id="element-531" />
              <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-532">
                Select a person to view messages
              </h3>
              <p className="text-gray-500" data-id="element-533">
                Choose a person from the dropdown to see or generate outreach
                messages.
              </p>
            </div>}
          {/* Mobile fixed action bar */}
          {selectedPerson && selectedMessage && <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 flex justify-between items-center lg:hidden" data-id="element-534">
              <div className="text-sm" data-id="element-535">
                <span className="font-medium" data-id="element-536">Selected:</span> Message #
                {selectedMessage}
              </div>
              <button onClick={handleSendMessage} className="inline-flex items-center px-4 py-2 bg-[#f9b30f] hover:bg-[#e0a40e] text-white text-sm font-medium rounded-md" data-id="element-537">
                <SendIcon size={16} className="mr-2" data-id="element-538" />
                Send Message
              </button>
            </div>}
        </div>
      </div>
    </div>;
};